/**
 * Écran du diagnostic de programme (migration 0318) — `/student/diagnostic`.
 * Intro (faire / reprendre / recommencer) → questions une par une (popup XP,
 * « Arrêter, je reprendrai plus tard ») → résultat : maîtrise du programme,
 * cartes à travailler / acquises, rapport rédigé par IA.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ClipboardCheck, PauseCircle, RotateCw, Sparkles } from 'lucide-react';
import {
  Button,
  FeedbackPopup,
  MarkdownMath,
  MathText,
  computeXpReward,
  type XpReward,
} from '@jeraisonne/ui';
import { ChoiceButton } from '@/components/exercise/choice-button';
import { PaperChoices, PaperColumn, PaperPage, usePaperPlayerForMe } from '@/features/paper-player';
import { freeSheetKey } from '@/features/scratch-sheet/use-free-sheet';
import { QuestionVisuals } from '@/components/exercise/question-visuals';
import { HelpTip } from '@/components/help-tip';
import { Spinner } from '@/components/spinner';
import { useAuth } from '@/lib/auth';
import {
  DIAGNOSTIC_REPORT_MIN_ANSWERS,
  DiagnosticError,
  canRestartDiagnostic,
  canSeeReport,
  diagnosticErrorMessage,
  diagnosticStateKey,
  finishDiagnostic,
  restartDateLabel,
  resumeIndex,
  startDiagnostic,
  submitDiagnosticAnswer,
  useDiagnosticResult,
  useDiagnosticState,
  useGenerateDiagnosticReport,
  type DiagnosticReport,
  type DiagnosticSession,
  type DiagnosticSummary,
} from '@/lib/skill-diagnostic';

type Phase = 'intro' | 'loading' | 'playing' | 'finishing' | 'result' | 'error';

type ExitPath = '/student/dashboard' | '/parent/dashboard' | '/player/dashboard';

/** Bilan d'un adulte (migration 0328) : mêmes questions / fin / résultat, autre départ. */
export interface AdultDiagnosticMode {
  start: (restart: boolean) => Promise<DiagnosticSession>;
  exitTo: ExitPath;
  /** Écran d'accueil (choix du niveau…) ; `begin` lance, `showResult` ouvre un bilan. */
  renderIntro: (helpers: {
    begin: (restart: boolean) => void;
    showResult: (diagnosticId: string) => void;
  }) => React.ReactNode;
  onRefresh?: () => void;
}

/** Retire le markdown simple (`**gras**`) : le rapport est affiché en texte. */
function plain(text: string): string {
  return text.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1');
}

export function DiagnosticPage({
  autoStart = false,
  adult,
}: { autoStart?: boolean; adult?: AdultDiagnosticMode } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const state = useDiagnosticState(user?.id, !adult);
  const exitTo: ExitPath = adult?.exitTo ?? '/student/dashboard';
  const [phase, setPhase] = useState<Phase>('intro');
  const [session, setSession] = useState<DiagnosticSession | null>(null);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  // Exercice « sur la feuille de brouillon » (drapeau `paper_player`).
  const paper = usePaperPlayerForMe();
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultId, setResultId] = useState<string | null>(null);
  const [streak, setStreak] = useState(0);
  const [sessionXp, setSessionXp] = useState(0);
  const [reward, setReward] = useState<{
    value: XpReward;
    wasCorrect: boolean;
    explanationMd?: string;
    errorFeedbackMd?: string;
  } | null>(null);
  const shownAt = useRef(Date.now());

  const onAdultRefresh = adult?.onRefresh;
  const refreshState = useCallback(() => {
    void qc.invalidateQueries({ queryKey: diagnosticStateKey(user?.id ?? '') });
    onAdultRefresh?.();
  }, [qc, user?.id, onAdultRefresh]);

  async function begin(restart: boolean) {
    setPhase('loading');
    setError(null);
    try {
      const s = adult ? await adult.start(restart) : await startDiagnostic(restart);
      setSession(s);
      const i = resumeIndex(s);
      setStreak(0);
      setSessionXp(0);
      if (i >= s.questions.length) {
        await finish(s.diagnostic.id);
        return;
      }
      setIndex(i);
      setSelected(null);
      setRevealed(false);
      shownAt.current = Date.now();
      setPhase('playing');
      refreshState();
    } catch (e) {
      setError(diagnosticErrorMessage(e instanceof DiagnosticError ? e.code : ''));
      setPhase('error');
    }
  }

  async function finish(diagnosticId: string) {
    setPhase('finishing');
    try {
      await finishDiagnostic(diagnosticId);
      setResultId(diagnosticId);
      setPhase('result');
      refreshState();
      void qc.invalidateQueries({ queryKey: ['student-skillcards'] });
      void qc.invalidateQueries({ queryKey: ['student-profile'] });
    } catch (e) {
      setError(diagnosticErrorMessage(e instanceof DiagnosticError ? e.code : ''));
      setPhase('error');
    }
  }

  const question = session?.questions[index];

  async function answer(choiceId: string) {
    if (!session || !question || revealed || submitting) return;
    setSelected(choiceId);
    setSubmitting(true);
    try {
      const r = await submitDiagnosticAnswer({
        diagnosticId: session.diagnostic.id,
        questionId: question.step_id,
        choiceId,
        timeMs: Date.now() - shownAt.current,
      });
      setRevealed(true);
      const xp = computeXpReward(r.was_correct, streak);
      setStreak(xp.streak);
      setSessionXp((t) => t + xp.total);
      setReward({
        value: xp,
        wasCorrect: r.was_correct,
        explanationMd: r.explanation_md ?? undefined,
        errorFeedbackMd: r.feedback_md ?? undefined,
      });
    } catch (e) {
      setError(diagnosticErrorMessage(e instanceof DiagnosticError ? e.code : ''));
      setPhase('error');
    } finally {
      setSubmitting(false);
    }
  }

  function next() {
    if (!session) return;
    if (index < session.questions.length - 1) {
      setIndex((i) => i + 1);
      setSelected(null);
      setRevealed(false);
      shownAt.current = Date.now();
      return;
    }
    void finish(session.diagnostic.id);
  }

  // ── Rendu ────────────────────────────────────────────────────────────────
  const current = state.data?.current;
  const lastDone = state.data?.last_completed;

  // Arrivée depuis l'onboarding (`?start=1`) : on lance (ou reprend) tout de suite.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!autoStart || autoStarted.current || !state.data?.available) return;
    if (state.data.current?.status === 'completed') return;
    autoStarted.current = true;
    void begin(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lancement unique
  }, [autoStart, state.data]);

  if (phase === 'loading' || phase === 'finishing') {
    return (
      <Shell backTo={exitTo}>
        <p role="status" className="text-ink-500 flex items-center gap-2 text-sm">
          <Spinner className="h-4 w-4" />
          {phase === 'finishing' ? 'Calcul de ton résultat…' : 'Préparation du diagnostic…'}
        </p>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell backTo={exitTo}>
        <p role="alert" className="text-error-700 bg-error-50 rounded-xl px-4 py-3 text-sm">
          {error}
        </p>
        <Button className="mt-4" fullWidth onClick={() => setPhase('intro')}>
          Retour
        </Button>
      </Shell>
    );
  }

  if (phase === 'result' && resultId) {
    return (
      <Shell backTo={exitTo}>
        <DiagnosticResultView
          diagnosticId={resultId}
          audience={adult ? 'adult' : 'student'}
          onRestart={() => (adult ? setPhase('intro') : void begin(true))}
          onResume={() => void begin(false)}
        />
      </Shell>
    );
  }

  if (phase === 'playing' && session && question) {
    const total = session.questions.length;
    return (
      <Shell backTo={exitTo}>
        <div className="mb-4 space-y-2">
          <div className="text-ink-600 flex items-center justify-between text-xs font-semibold">
            <span>
              Question {index + 1} / {total}
            </span>
            {question.card_title && (
              <span className="text-ink-500 truncate pl-3">
                <MathText>{question.card_title}</MathText>
              </span>
            )}
          </div>
          <div className="bg-ink-100 h-1.5 overflow-hidden rounded-full">
            <div
              className="bg-primary-500 h-full rounded-full transition-all"
              style={{ width: `${((index + 1) / total) * 100}%` }}
            />
          </div>
        </div>
        {paper ? (
          <PaperPage className="-mx-4 min-h-0 sm:rounded-2xl">
            <PaperColumn className="pt-3">
              <div className="text-ink-900 mb-3 text-base">
                <MarkdownMath>{question.prompt_md}</MarkdownMath>
              </div>
              <div className="pp-figure">
                <QuestionVisuals visuals={question.visuals} />
              </div>
              <PaperChoices
                questionKey={question.step_id}
                promptMd={question.prompt_md}
                choices={question.choices}
                selected={selected}
                revealed={revealed}
                busy={submitting}
                onChoose={(id) => void answer(id)}
                storageKey={freeSheetKey('diagnostic', session?.diagnostic.id, question.step_id)}
              />
            </PaperColumn>
          </PaperPage>
        ) : (
          <>
            <div className="text-ink-900 mb-4 text-base">
              <MarkdownMath>{question.prompt_md}</MarkdownMath>
            </div>
            <QuestionVisuals visuals={question.visuals} />
            <div className="space-y-2">
              {question.choices.map((choice, i) => (
                <ChoiceButton
                  key={choice.id}
                  choice={choice}
                  index={i}
                  selected={selected === choice.id}
                  revealed={revealed}
                  onClick={() => void answer(choice.id)}
                />
              ))}
            </div>
          </>
        )}
        <Button
          variant="ghost"
          fullWidth
          className="mt-6"
          onClick={() => {
            refreshState();
            void navigate({ to: exitTo });
          }}
        >
          <PauseCircle className="h-4 w-4" aria-hidden /> Arrêter, je reprendrai plus tard
        </Button>
        {reward && (
          <FeedbackPopup
            reward={reward.value}
            wasCorrect={reward.wasCorrect}
            sessionXp={sessionXp}
            explanationMd={reward.explanationMd}
            errorFeedbackMd={reward.errorFeedbackMd}
            onClose={() => {
              setReward(null);
              next();
            }}
          />
        )}
      </Shell>
    );
  }

  // Intro
  if (adult) {
    return (
      <Shell backTo={exitTo}>
        {adult.renderIntro({ begin: (r) => void begin(r), showResult: setResultIdAndShow })}
      </Shell>
    );
  }
  return (
    <Shell backTo={exitTo}>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="text-primary-600 h-6 w-6" aria-hidden />
          <h1 className="font-display text-ink-900 text-2xl font-extrabold">Mon diagnostic</h1>
          <HelpTip topic="diagnostic" label="Diagnostic" />
        </div>
        <p className="text-ink-600 text-sm">
          Une quinzaine de questions sur les SKILLCARDS que tu as déjà vues (début de l’année et
          année précédente). Tu peux t’arrêter et reprendre quand tu veux. À la fin : ta maîtrise du
          programme, les cartes à retravailler et un rapport personnalisé.
        </p>
        {state.isLoading ? (
          <div className="bg-ink-100 h-12 animate-pulse rounded-xl" />
        ) : !state.data?.available ? (
          <p className="text-ink-500 text-sm">
            Le diagnostic est réservé aux élèves dont le niveau est indiqué dans le profil.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {current?.status === 'in_progress' ? (
              <>
                <Button fullWidth onClick={() => void begin(false)}>
                  Reprendre ({current.answered_count}/{current.question_count})
                </Button>
                {canSeeReport(current) && (
                  <ReportLink diagnosticId={current.id} label="Voir mon rapport partiel" />
                )}
                <RestartButton
                  current={current}
                  label="Recommencer à zéro"
                  onRestart={() => void begin(true)}
                />
              </>
            ) : current?.status === 'completed' ? (
              <>
                <Button fullWidth onClick={() => setResultIdAndShow(current.id)}>
                  Voir mon dernier diagnostic
                </Button>
                <RestartButton
                  current={current}
                  label="Recommencer le diagnostic"
                  onRestart={() => void begin(true)}
                />
              </>
            ) : (
              <Button fullWidth onClick={() => void begin(false)}>
                Commencer le diagnostic
              </Button>
            )}
            {lastDone && lastDone.id !== current?.id && (
              <Button variant="ghost" fullWidth onClick={() => setResultIdAndShow(lastDone.id)}>
                Voir le précédent ({lastDone.mastery_pct ?? 0} %)
              </Button>
            )}
          </div>
        )}
      </div>
    </Shell>
  );

  function setResultIdAndShow(id: string) {
    setResultId(id);
    setPhase('result');
  }
}

/** Page du rapport d'un diagnostic (`/student/diagnostic/rapport/$id`). */
export function DiagnosticReportPage({ diagnosticId }: { diagnosticId: string }) {
  const navigate = useNavigate();
  const toDiagnostic = () => void navigate({ to: '/student/diagnostic' });
  return (
    <Shell>
      <DiagnosticResultView
        diagnosticId={diagnosticId}
        onRestart={toDiagnostic}
        onResume={toDiagnostic}
      />
    </Shell>
  );
}

/** Lien vers le rapport (partiel dès 5 réponses). */
export function ReportLink({ diagnosticId, label }: { diagnosticId: string; label: string }) {
  return (
    <Link
      to="/student/diagnostic/rapport/$diagnosticId"
      params={{ diagnosticId }}
      className="text-primary-700 border-primary-200 hover:bg-primary-50 inline-flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 rounded-xl border text-sm font-semibold active:scale-[0.98]"
    >
      <Sparkles className="h-4 w-4" aria-hidden /> {label}
    </Link>
  );
}

/** Recommencer : seulement un mois après le début du diagnostic précédent. */
function RestartButton({
  current,
  label,
  onRestart,
}: {
  current: Pick<DiagnosticSummary, 'restart_available_at'> | null | undefined;
  label: string;
  onRestart: () => void;
}) {
  const allowed = canRestartDiagnostic(current);
  return (
    <div className="space-y-1">
      <Button variant="outline" fullWidth disabled={!allowed} onClick={onRestart}>
        <RotateCw className="h-4 w-4" aria-hidden /> {label}
      </Button>
      {!allowed && (
        <p className="text-ink-500 text-center text-xs">
          Nouveau diagnostic possible le {restartDateLabel(current?.restart_available_at)}.
        </p>
      )}
    </div>
  );
}

function Shell({
  children,
  backTo = '/student/dashboard',
}: {
  children: React.ReactNode;
  backTo?: ExitPath;
}) {
  return (
    <div className="mx-auto max-w-lg px-4 py-6 pb-24 sm:py-8 lg:max-w-2xl">
      <Link
        to={backTo}
        className="text-primary-600 mb-4 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />{' '}
        {backTo === '/player/dashboard' ? 'Tableau de bord' : 'Accueil'}
      </Link>
      {children}
    </div>
  );
}

function ReportList({ title, items, tone }: { title: string; items: string[]; tone: string }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className={`text-xs font-bold uppercase tracking-wider ${tone}`}>{title}</p>
      <ul className="text-ink-700 mt-1 list-disc space-y-0.5 pl-5 text-sm">
        {items.map((t) => (
          <li key={t}>{plain(t)}</li>
        ))}
      </ul>
    </div>
  );
}

function ReportView({
  report,
  partial,
  audience = 'student',
}: {
  report: DiagnosticReport;
  partial?: boolean;
  audience?: 'student' | 'parent';
}) {
  const own = audience === 'student';
  return (
    <div className="border-primary-100 bg-primary-50/50 space-y-3 rounded-2xl border p-4">
      <p className="text-primary-700 flex items-center gap-1.5 text-sm font-bold">
        <Sparkles className="h-4 w-4" aria-hidden />{' '}
        {own
          ? partial
            ? 'Ton rapport partiel'
            : 'Ton rapport'
          : partial
            ? 'Rapport partiel'
            : 'Rapport du diagnostic'}
      </p>
      <p className="text-ink-800 whitespace-pre-line text-sm">{plain(report.summary_md)}</p>
      {/* Forces et lacunes : pas reprises ici, les listes de SKILLCARDS
          « Points forts » / « Faiblesses à travailler » les montrent dessous. */}
      <ReportList title="Conseils" items={report.advice} tone="text-primary-700" />
    </div>
  );
}

export function DiagnosticResultView({
  diagnosticId,
  onRestart,
  onResume,
  audience = 'student',
}: {
  diagnosticId: string;
  onRestart?: () => void;
  /** Diagnostic en pause : reprendre là où l'élève s'était arrêté. */
  onResume?: () => void;
  /** `parent` (migration 0325) : lecture seule, sans reprise ni nouveau diagnostic.
   *  `adult` (0328) : bilan d'un adulte — pas d'XP, « Faire un autre bilan ». */
  audience?: 'student' | 'parent' | 'adult';
}) {
  const isParent = audience === 'parent';
  const isAdult = audience === 'adult';
  const result = useDiagnosticResult(diagnosticId);
  const generate = useGenerateDiagnosticReport(diagnosticId);
  const asked = useRef(false);
  const d = result.data?.diagnostic;
  const answered = d?.answered_count ?? 0;
  const stale =
    !!result.data &&
    answered >= DIAGNOSTIC_REPORT_MIN_ANSWERS &&
    (!result.data.report || (d?.report_answered ?? 0) < answered);
  const report = generate.data ?? (stale ? null : (result.data?.report ?? null));

  // Rapport rédigé à l'ouverture s'il manque ou ne couvre pas toutes les
  // réponses (rapport partiel puis mis à jour) ; le serveur le garde ensuite.
  useEffect(() => {
    if (!stale || asked.current) return;
    asked.current = true;
    generate.mutate();
  }, [stale, generate]);

  if (result.isLoading || !result.data || !d) {
    return <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />;
  }
  const partial = result.data.partial === true || d.status !== 'completed';
  const cards = result.data.cards;
  const correct = partial ? cards.reduce((n, c) => n + c.correct, 0) : (d.correct ?? 0);
  const total = partial ? cards.reduce((n, c) => n + c.total, 0) : (d.total ?? 0);
  const pct = partial
    ? total > 0
      ? Math.round((100 * correct) / total)
      : 0
    : (d.mastery_pct ?? 0);
  const toWork = cards.filter((c) => c.needs_work);
  const acquired = cards.filter((c) => !c.needs_work);

  return (
    <div className="space-y-5">
      {partial && (
        <div className="ocean:bg-amber-500/10 space-y-2 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="ocean:text-amber-200 text-sm font-semibold text-amber-800">
            Rapport partiel · {answered} / {d.question_count} questions
          </p>
          <p className="text-ink-600 text-xs">
            {isParent
              ? 'Le diagnostic n’est pas terminé : le rapport se mettra à jour.'
              : 'Termine le diagnostic pour un résultat complet : le rapport se mettra à jour.'}
          </p>
          {!isParent && onResume && (
            <Button fullWidth onClick={onResume}>
              Reprendre le diagnostic
            </Button>
          )}
        </div>
      )}

      <div className="text-center">
        <p className="text-ink-500 text-xs font-bold uppercase tracking-wider">
          Maîtrise du programme{partial ? ' (provisoire)' : ''}
        </p>
        <p className="font-display text-ink-900 text-4xl font-extrabold">{pct}%</p>
        <p className="text-ink-500 text-sm">
          {correct} / {total} bonnes réponses
          {!partial && audience === 'student' && ` · +${d.xp_awarded ?? 0} XP`}
        </p>
      </div>

      {answered < DIAGNOSTIC_REPORT_MIN_ANSWERS ? (
        <p className="text-ink-500 text-sm">
          {isParent
            ? `Le rapport sera disponible après ${DIAGNOSTIC_REPORT_MIN_ANSWERS} réponses.`
            : `Réponds à au moins ${DIAGNOSTIC_REPORT_MIN_ANSWERS} questions pour obtenir ton rapport.`}
        </p>
      ) : report ? (
        <ReportView report={report} partial={partial} audience={isAdult ? 'student' : audience} />
      ) : generate.isError ? (
        <div className="space-y-2">
          <p className="text-ink-500 text-sm">Le rapport n’a pas pu être rédigé.</p>
          <Button variant="outline" onClick={() => generate.mutate()}>
            Réessayer
          </Button>
        </div>
      ) : (
        <p role="status" className="text-ink-500 flex items-center gap-2 text-sm">
          <Spinner className="h-4 w-4" /> Rédaction du rapport…
        </p>
      )}

      {acquired.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-display text-ink-900 text-base font-bold">
            Points forts ({acquired.length})
          </h2>
          <CardLinks cards={acquired} />
        </section>
      )}
      {toWork.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-display text-ink-900 text-base font-bold">
            Faiblesses à travailler ({toWork.length})
          </h2>
          {!partial && !isAdult && (
            <p className="text-ink-500 text-xs">
              {isParent
                ? 'Ajoutées à ses SKILLCARDS « à travailler ».'
                : 'Ajoutées à tes SKILLCARDS « à travailler ».'}
            </p>
          )}
          <CardLinks cards={toWork} />
        </section>
      )}

      {!partial && audience === 'student' && onRestart && (
        <RestartButton current={d} label="Recommencer le diagnostic" onRestart={onRestart} />
      )}
      {isAdult && onRestart && (
        <Button variant="outline" fullWidth onClick={onRestart}>
          <RotateCw className="h-4 w-4" aria-hidden /> Faire un autre bilan
        </Button>
      )}
    </div>
  );
}

function CardLinks({
  cards,
}: {
  cards: { card_id: string; title: string | null; correct: number; total: number }[];
}) {
  return (
    <ul className="space-y-2">
      {cards.map((c) => (
        <li key={c.card_id}>
          <Link
            to="/card/$cardId"
            params={{ cardId: c.card_id }}
            className="border-ink-200 flex min-h-[2.75rem] items-center justify-between gap-3 rounded-xl border bg-white px-4 py-2 text-sm active:scale-[0.99]"
          >
            <span className="text-ink-900 min-w-0 font-semibold">
              <MathText>{c.title ?? 'SKILLCARD'}</MathText>
            </span>
            <span className="text-ink-500 shrink-0 text-xs">
              {c.correct}/{c.total}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
